import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { DollarSign, Plus, Tag } from 'lucide-react';
import { CostTable } from '../components/CostTable';
import { CostForm } from '../components/CostForm';

const CATEGORIES = {
  software: { label: 'Software', color: 'bg-blue-100 text-blue-700' },
  hosting: { label: 'Hosting', color: 'bg-purple-100 text-purple-700' },
  api: { label: 'API Services', color: 'bg-green-100 text-green-700' },
  marketing: { label: 'Marketing', color: 'bg-orange-100 text-orange-700' },
  tools: { label: 'Tools', color: 'bg-teal-100 text-teal-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-700' },
};

export default function AdminSystemCostsPage() {
  const { api } = useAuth();
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', amount: '', category: 'software', notes: '', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [activeTags, setActiveTags] = useState([]);

  useEffect(() => { loadCosts(); }, []);
  const loadCosts = async () => { try { const r = await api.get('/api/admin/system-costs'); setCosts(r.data || []); } catch {} finally { setLoading(false); } };

  const handleSave = async () => {
    if (!form.name || !form.amount) { toast.error('Name and amount required'); return; }
    const p = { ...form, amount: parseFloat(form.amount) };
    try { if (editingId) { await api.put('/api/admin/system-costs/' + editingId, p); } else { await api.post('/api/admin/system-costs', p); } toast.success('Saved'); resetForm(); loadCosts(); } catch { toast.error('Failed'); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete('/api/admin/system-costs/' + id); toast.success('Deleted'); loadCosts(); } catch {} };
  const handleEdit = (c) => { setEditingId(c.id); setForm({ name: c.name, amount: c.amount.toString(), category: c.category, notes: c.notes || '', tags: c.tags || [] }); setShowForm(true); };
  const resetForm = () => { setEditingId(null); setForm({ name: '', amount: '', category: 'software', notes: '', tags: [] }); setTagInput(''); setShowForm(false); };
  const addTag = () => { const t = tagInput.trim(); if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] }); setTagInput(''); };

  const fmt = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);
  const allTags = [...new Set(costs.flatMap(c => c.tags || []))];
  const filtered = activeTags.length > 0 ? costs.filter(c => (c.tags || []).some(t => activeTags.includes(t))) : costs;
  const total = filtered.reduce((s, c) => s + (c.amount || 0), 0);
  const hasFilter = activeTags.length > 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#84CC16] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-[#0A1628]">Monthly System Costs</h1><p className="text-gray-500">Track recurring expenses</p></div>
        {!showForm && <Button onClick={() => setShowForm(true)} className="bg-[#84CC16] hover:bg-[#65a30d] text-white rounded-xl"><Plus className="w-4 h-4 mr-2" /> Add Cost</Button>}
      </div>
      {allTags.length > 0 && <TagFilterBar allTags={allTags} activeTags={activeTags} toggle={(t) => setActiveTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t])} clear={() => setActiveTags([])} />}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label={hasFilter ? 'Filtered Monthly' : 'Total Monthly'} value={fmt(total)} />
        <StatCard label="Annual Projection" value={fmt(total * 12)} color="text-[#014DB7]" />
        <StatCard label={hasFilter ? 'Filtered Items' : 'Active Items'} value={filtered.length} color="text-[#84CC16]" />
      </div>
      {showForm && <CostForm form={form} setForm={setForm} tagInput={tagInput} setTagInput={setTagInput} addTag={addTag} removeFormTag={(t) => setForm({...form, tags: form.tags.filter(x=>x!==t)})} onSave={handleSave} onCancel={resetForm} editingId={editingId} />}
      {filtered.length === 0 ? <EmptyBox hasFilter={hasFilter} /> : <CostTable costs={filtered} categories={CATEGORIES} fmt={fmt} onEdit={handleEdit} onDelete={handleDelete} totalMonthly={total} />}
    </div>
  );
}

const StatCard = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5"><p className="text-xs text-gray-400 uppercase font-semibold mb-1">{label}</p><p className={`text-2xl font-bold ${color || 'text-[#0A1628]'}`}>{value}</p></div>
);

const TagFilterBar = ({ allTags, activeTags, toggle, clear }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-gray-500 uppercase mr-2"><Tag className="w-3 h-3 inline mr-1" />Filter:</span>
      {allTags.map(t => <button key={t} onClick={() => toggle(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTags.includes(t) ? 'bg-[#014DB7] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t}</button>)}
      {activeTags.length > 0 && <button onClick={clear} className="text-xs text-red-500 hover:underline ml-2">Clear</button>}
    </div>
  </div>
);

const EmptyBox = ({ hasFilter }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
    <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
    <h3 className="font-semibold text-gray-700 mb-2">{hasFilter ? 'No costs match selected tags' : 'No System Costs'}</h3>
    <p className="text-gray-500">{hasFilter ? 'Try different tags.' : 'Add your monthly recurring costs.'}</p>
  </div>
);
